/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { FlexCol, Spinner } from 'sarsaparilla';
import { distance } from '@turf/distance';
import { lineString } from '@turf/helpers';
import { point } from 'shared-ui/maps/turfUtils';
import { createLineBuffer } from 'shared-ui/maps/helpers/lineBuffer';

import MapContainer from '../components/MapContainer';
import TripSummary from '../components/TripSummary';
import * as tripPlannerActions from '../actions/tripPlanner';
import { convertSearchResultsToGEOJson } from '../utils/mapUtils';
import { sortByDistance } from '../utils/tripUtils';
import TripResultsViewSwitch from '../components/TripResultsViewSwitch';
import TripSearchResultListContainer from './TripSearchResultListContainer';
import AddTripItemModal from '../components/AddTripItemModal';

function TripContentSearchResultsContainer() {
    const dispatch = useDispatch();
    const tripPlanner = useSelector((state) => state.tripPlanner);
    const tripPlannerSearchResults = useSelector(
        (state) => state.tripPlannerSearchResults
    );

    const [geoResults, setGeoResults] = useState(null);
    const [uiFilters, setUiFilters] = useState([]);
    const [mapLocationData, setMapLocationData] = useState(null);
    const [routeLine, setRouteLine] = useState(null);
    const [routePolygon, setRoutePolygon] = useState(null);
    const [detailedRouteLine, setDetailedRouteLine] = useState(null);
    const [directions, setDirections] = useState([]);
    const [radius, setRadius] = useState(tripPlanner.trip.radius);
    const [startDate, setStartDate] = useState(tripPlanner.trip.start_date);
    const [fg, setFg] = useState(tripPlanner.fg);
    const [line, setLine] = useState(null);
    const [bufferPolygon, setBufferPolygon] = useState(null);

    const getBufferPolygon = useCallback(async () => {
        if (line && radius > 0) {
            // We want to use turf.buffer when they remove jsts dependency for this module
            const polygon = await createLineBuffer(line, radius);
            setBufferPolygon(polygon);
        }
    }, [line, radius]);

    useEffect(() => {
        getBufferPolygon();
    }, [line, radius]);

    useEffect(() => {
        if (
            tripPlannerSearchResults.geoResults &&
            !isEqual(tripPlannerSearchResults.geoResults, geoResults)
        ) {
            const updatedSearchResult = convertSearchResultsToGEOJson(
                tripPlannerSearchResults.geoResults
            );
            setGeoResults(tripPlannerSearchResults.geoResults);
            setMapLocationData(updatedSearchResult);
        }
    }, [tripPlannerSearchResults.geoResults]);

    useEffect(() => {
        if (
            tripPlannerSearchResults.routeLine &&
            !isEqual(tripPlannerSearchResults.routeLine, routeLine)
        ) {
            setRouteLine(tripPlannerSearchResults.routeLine);
        }
    }, [tripPlannerSearchResults.routeLine]);

    useEffect(() => {
        if (
            tripPlannerSearchResults.detailedRouteLine &&
            !isEqual(tripPlannerSearchResults.detailedRouteLine, detailedRouteLine)
        ) {
            setDetailedRouteLine(tripPlannerSearchResults.detailedRouteLine);
        }
    }, [tripPlannerSearchResults.detailedRouteLine]);

    useEffect(() => {
        if (
            tripPlannerSearchResults.routePolygon &&
            !isEqual(tripPlannerSearchResults.routePolygon, routePolygon)
        ) {
            setRoutePolygon(tripPlannerSearchResults.routePolygon);
        }
    }, [tripPlannerSearchResults.routePolygon]);

    useEffect(() => {
        if (tripPlanner.uiFilters && !isEqual(tripPlanner.uiFilters, uiFilters)) {
            setUiFilters(tripPlanner.uiFilters);
        }
    }, [tripPlanner.uiFilters]);

    useEffect(() => {
        if (tripPlanner.fg && !isEqual(tripPlanner.fg, fg)) {
            setFg(tripPlanner.fg);
        }
    }, [tripPlanner.fg]);

    useEffect(() => {
        if (tripPlanner.directions && !isEqual(tripPlanner.directions, directions)) {
            setDirections(tripPlanner.directions);
            if (tripPlanner.directions?.routes?.[0]?.geometry?.coordinates) {
                setLine(
                    lineString(tripPlanner.directions.routes?.[0]?.geometry?.coordinates)
                );
            }
        }
    }, [tripPlanner.directions]);

    useEffect(() => {
        if (tripPlanner.trip.radius !== radius) {
            setRadius(tripPlanner.trip.radius);
        }
    }, [tripPlanner.trip.radius]);

    useEffect(() => {
        if (!isEqual(tripPlanner.trip.start_date, startDate)) {
            setStartDate(tripPlanner.trip.start_date);
        }
    }, [tripPlanner.trip.start_date]);

    useEffect(() => {
        if (line && (uiFilters || directions || radius || startDate || fg)) {
            dispatch(
                tripPlannerActions.fetchTripPlannerMapSearchResults(line, bufferPolygon)
            );
        }
    }, [
        bufferPolygon,
        directions,
        startDate,
        uiFilters,
        fg,
        tripPlanner.latSW,
        tripPlanner.lngSW,
        tripPlanner.latNE,
        tripPlanner.lngNE,
    ]);

    const onMapAddTripItem = useCallback(
        (item) => {
            // Always show the modal to let user choose destination
            dispatch(tripPlannerActions.setShowTripItemAddSModal(true, item));
        },
        [dispatch]
    );

    const onMapDeleteTripItem = (item) => {
        dispatch(tripPlannerActions.deleteItem(null, item));
    };

    const onMapMoveEnd = (map) => {
        const bounds = map.getBounds();
        const center = map.getCenter();

        dispatch(
            tripPlannerActions.updateTripPlannerCriterias({
                lat: center.lat,
                lng: center.lng,
                latSW: bounds._sw.lat,
                lngSW: bounds._sw.lng,
                latNE: bounds._ne.lat,
                lngNE: bounds._ne.lng,
            })
        );
    };

    const loading = tripPlanner.loading ? (
        <div className="trip-planner-loading">
            <Spinner size="sm" />
        </div>
    ) : null;

    const listView =
        tripPlanner.view === 'list' ? <TripSearchResultListContainer /> : null;
    const mapShown = tripPlanner.view === 'map';
    const routeLineToShow = detailedRouteLine || routeLine;

    return (
        <FlexCol
            className="trip-planner-search-results-container"
            data-testid="trip-planner-search-results-container"
        >
            {loading}
            <TripResultsViewSwitch />
            <TripSummary />
            {mapShown && (
                <div className="trip-planner-map">
                    <div id="map" className="trip-planner-results-map">
                        <MapContainer
                            mapContainer="map"
                            mapIsZoomByScrollEnabled
                            mapIsMapLayerControlVisible
                            mapZoom={15}
                            visible={mapShown}
                            routeLine={routeLineToShow}
                            routePolygon={routePolygon}
                            mapGeoJsonData={mapLocationData}
                            onMapMoveEnd={onMapMoveEnd}
                            onAddTripItem={onMapAddTripItem}
                            onDeleteTripItem={onMapDeleteTripItem}
                        />
                    </div>
                </div>
            )}
            {listView}
            <AddTripItemModal />
        </FlexCol>
    );
}

export default TripContentSearchResultsContainer;
